import { get, concat } from 'lodash-es';

import filterEmptySizeGroups from '~modules/core/js/sku-selector-utils';
import {
    shapeProductReviews,
    getProductBrandName,
    getProductDepartment,
    shapeFilterGroups,
    shapeSelectedFilterParams,
} from '~routes/product/js/product-utils';

import { SET_PRODUCT_ATTRIBUTION } from '~modules/core/js/store';
import { BOX_MODULE_NAME } from '~modules/box/js/box-store';
import { SITE_SETTINGS_MODULE_NAME } from '~modules/site-settings/js/site-settings-store';
import { previewPrePurchase } from '~modules/box/js/box-api';
import { getProductDetails } from '~routes/product/js/product-api';
import { getProductReviewsV2 } from '~routes/review/js/review-api';

import {
    DEFAULT_NUM_REVIEWS,
    DEFAULT_NUM_REVIEWS_IMAGE_ONLY,
    PRODUCT_PAGE_VIEW_MODES,
} from '~routes/product/js/product-constants';
import { REVIEW_SCHEMES } from '~routes/review/js/review-constants';
import { rentalRouteNames } from '~modules/core/js/router-constants';
import { PRODUCT_AFFILIATION, PRODUCT_BUSINESSES } from '~modules/analytics/js/analytics-constants';

export const PRODUCT_MODULE_NAME = 'product';
export const SET_PRODUCT_DETAILS = 'SET_PRODUCT_DETAILS';
export const SET_PRODUCT_CLOSET_STATUS = 'SET_PRODUCT_CLOSET_STATUS';
export const SET_PRODUCT_REVIEWS = 'SET_PRODUCT_REVIEWS';
export const RESET_PRODUCT_REVIEWS = 'RESET_PRODUCT_REVIEWS';
export const RESET_PRODUCT_REVIEWS_IMAGES_ONLY = 'RESET_PRODUCT_REVIEWS_IMAGES_ONLY';
export const RESET_PRODUCT_REVIEW_SUMMARY = 'RESET_PRODUCT_REVIEW_SUMMARY';
export const SET_PRODUCT_REVIEWS_IMAGE_ONLY = 'SET_PRODUCT_REVIEWS_IMAGE_ONLY';
export const SET_PRODUCT_REVIEWS_SELECTED_SIZES = 'SET_PRODUCT_REVIEWS_SELECTED_SIZES';
export const SET_PRODUCT_REVIEWS_FILTER_GROUP = 'SET_PRODUCT_REVIEWS_FILTER_GROUP';
export const SET_PRODUCT_REVIEWS_FILTER_ERROR = 'SET_PRODUCT_REVIEWS_FILTER_ERROR';
export const SET_COLOR_CODE = 'SET_COLOR_CODE';
export const SET_VIEW_MODE = 'SET_VIEW_MODE';

// TODO: for this ticket, it doesn't include refactor add to box stuff, just keep add to box function working as before;
export const SET_SKU_CODE = 'SET_SKU_CODE';
export const SET_SKU_SIZE_CODE = 'SET_SKU_SIZE_CODE';
export const SET_TEST_SKU_CODE = 'SET_TEST_SKU_CODE';
export const SET_ADD_TO_BOX_ERROR = 'SET_ADD_TO_BOX_ERROR';
export const SET_PRE_PURCHASE_PREVIEW = 'SET_PRE_PURCHASE_PREVIEW';
export const UPDATE_ITEM_IN_BOX_STATUS = 'UPDATE_ITEM_IN_BOX_STATUS';

export const FETCH_PRODUCT_DETAILS = 'FETCH_PRODUCT_DETAILS';
export const FETCH_PRODUCT_REVIEWS = 'FETCH_PRODUCT_REVIEWS';
export const FETCH_PRODUCT_REVIEWS_IMAGE_ONLY = 'FETCH_PRODUCT_REVIEWS_IMAGE_ONLY';
export const PREVIEW_PRE_PURCHASE = 'PREVIEW_PRE_PURCHASE';
export const WRITE_REVIEW = 'WRITE_REVIEW';

export default {
    namespaced: true,

    state: () => ({
        productDetails: {},
        traceId: '',
        colorCode: '',
        viewMode: PRODUCT_PAGE_VIEW_MODES.RENT,
        reviews: {
            allReviews: [],
            imageOnlyReviews: [],
            filterGroups: [],
            sortOptions: [],
            pagination: {},
            selectedSizes: {},
            selectedFilters: {},
            selectedFilterGroup: '',
            selectedFilterErrors: {},
        },
        itemIsInBox: false,
        prePurchasePreview: {},
        addToBoxError: '',
        selectedSku: '',
        selectedSkuSizeCode: '',
        catalogResponseId: '',
        curation: '',
    }),

    /* eslint-disable no-param-reassign */
    mutations: {
        [SET_PRODUCT_DETAILS](state, {
            productDetails,
            color,
            traceId = '',
            viewMode,
            catalogResponseId = '',
            curationName = '',
        }) {
            state.productDetails = productDetails;
            state.colorCode = color;
            state.traceId = traceId;
            state.viewMode = viewMode;
            state.catalogResponseId = catalogResponseId;
            state.curation = curationName;
        },
        [SET_PRODUCT_CLOSET_STATUS](state, { choiceId, newClosetStatus }) {
            const targetProduct = state.productDetails.choices.find(choice => choice.choiceId === choiceId);
            targetProduct.inCloset = newClosetStatus;
        },
        [SET_PRODUCT_REVIEWS_IMAGE_ONLY](state, { reviews, replaceItems }) {
            if (replaceItems) {
                state.reviews.imageOnlyReviews = reviews;
            } else {
                state.reviews.imageOnlyReviews = concat(state.reviews.imageOnlyReviews, reviews);
            }
        },
        [SET_PRODUCT_REVIEWS_FILTER_ERROR](state, error) {
            state.reviews.selectedFilterErrors = { ...state.reviews.selectedFilterErrors, ...error };
        },
        [SET_PRODUCT_REVIEWS_SELECTED_SIZES](state, sizes) {
            state.reviews.selectedSizes = sizes;
        },
        [SET_PRODUCT_REVIEWS](state, {
            reviews,
            replaceItems,
            filterGroups,
            sortOptions,
            pagination,
            selectedFilters,
            selectedFilterErrors,
        }) {
            reviews = replaceItems ? reviews : concat(state.reviews.allReviews, reviews);

            state.reviews.allReviews = shapeProductReviews(reviews);
            state.reviews.filterGroups = shapeFilterGroups(filterGroups, selectedFilters);
            state.reviews.sortOptions = sortOptions || [];
            state.reviews.pagination = pagination || {};
            state.reviews.selectedFilters = selectedFilters || {};
            state.reviews.selectedFilterErrors = selectedFilterErrors || {};
        },
        [SET_PRODUCT_REVIEWS_FILTER_GROUP](state, selectedFilter, selectedFilterErrors = {}) {
            selectedFilter = state.reviews.selectedFilterGroup === selectedFilter ? '' :
                selectedFilter;
            state.reviews.selectedFilterGroup = selectedFilter;
            state.reviews.selectedFilterErrors = selectedFilterErrors;
        },
        [RESET_PRODUCT_REVIEWS](state) {
            state.reviews.allReviews = [];
        },
        [RESET_PRODUCT_REVIEWS_IMAGES_ONLY](state) {
            state.reviews.imageOnlyReviews = [];
        },
        [RESET_PRODUCT_REVIEW_SUMMARY](state) {
            state.productDetails.reviewSummary = {
                fitRating1: 0,
                fitRating2: 0,
                fitRating3: 0,
                fitRating4: 0,
                fitRating5: 0,
                fitRatingAverage: 0,
                fitRatingCount: 0,
                imageCount: 0,
                overallRating1: 0,
                overallRating2: 0,
                overallRating3: 0,
                overallRating4: 0,
                overallRating5: 0,
                overallRatingAverage: 0,
                overallRatingCount: 0,
                reviewCount: 0,
            };
        },
        [SET_COLOR_CODE](state, color) {
            state.colorCode = color;
        },

        // TODO: it will be refactored in the future;
        [SET_SKU_CODE](state, sku) {
            state.selectedSku = sku;
        },
        [SET_SKU_SIZE_CODE](state, sizeCode) {
            state.selectedSkuSizeCode = sizeCode;
        },
        [SET_ADD_TO_BOX_ERROR](state, error) {
            state.addToBoxError = error;
        },
        [UPDATE_ITEM_IN_BOX_STATUS](state, status) {
            state.itemIsInBox = status;
        },
        [SET_VIEW_MODE](state, view) {
            state.viewMode = view;
        },
        [SET_PRE_PURCHASE_PREVIEW](state, preview) {
            state.prePurchasePreview = preview;
        },
    },

    actions: {
        [FETCH_PRODUCT_DETAILS]({ commit, rootState },
            { slug, color, view, catalogResponseId = '', curationName = '' }) {
            const dcOverride = rootState[SITE_SETTINGS_MODULE_NAME]?.inventoryDCPoolOverride;
            const viewMode = view || PRODUCT_PAGE_VIEW_MODES.RENT;

            return getProductDetails({
                r15Svc: this.$r15Svc,
                productSlug: slug,
                params: { color, view: viewMode },
                dcOverride,
                catalogResponseId,
            }).then((response) => {
                commit(SET_PRODUCT_DETAILS, {
                    productDetails: response,
                    color,
                    viewMode,
                    traceId: response.responseHeaders?.traceId,
                    catalogResponseId,
                    curationName,
                });

                return Promise.resolve(response);
            }).catch((error) => {
                this.$logger.debugError('Failed to fetch the product details: ', error);
                return Promise.reject(error);
            }).finally(() => {
                commit(SET_PRODUCT_ATTRIBUTION, {}, { root: true });
            });
        },
        [FETCH_PRODUCT_REVIEWS_IMAGE_ONLY]({ commit, state }, {
            pageNumber,
            itemsPerPage,
            replaceItems = false,
        } = {}) {
            pageNumber = pageNumber || 1;
            itemsPerPage = itemsPerPage || DEFAULT_NUM_REVIEWS_IMAGE_ONLY;

            return getProductReviewsV2({
                r15Svc: this.$r15Svc,
                styleNumber: state.productDetails.styleNumber,
                pageNumber,
                itemsPerPage,
                imageOnly: true,
            })
                .then(response => commit(SET_PRODUCT_REVIEWS_IMAGE_ONLY, { reviews: response.reviews, replaceItems }))
                .catch((error) => {
                    this.$logger.debugError('Failed to fetch the product reviews - images only: ', error);
                    return Promise.reject(error);
                });
        },
        [FETCH_PRODUCT_REVIEWS]({ commit, state }, {
            pageNumber,
            itemsPerPage,
            selectedFilters,
            replaceItems = true,
        } = {}) {
            pageNumber = pageNumber || 1;
            itemsPerPage = itemsPerPage || DEFAULT_NUM_REVIEWS;
            selectedFilters = { ...state.reviews.selectedFilters, ...selectedFilters };

            return getProductReviewsV2({
                r15Svc: this.$r15Svc,
                styleNumber: state.productDetails.styleNumber,
                pageNumber,
                itemsPerPage,
                params: shapeSelectedFilterParams(selectedFilters),
            })
                .then(response => commit(SET_PRODUCT_REVIEWS, { ...response, replaceItems, selectedFilters }))
                .catch((error) => {
                    this.$logger.debugError('Failed to fetch all product reviews: ', error);
                    return Promise.reject(error);
                });
        },
        async [PREVIEW_PRE_PURCHASE]({ commit }, { r15Svc, skuId, couponCode }) {
            try {
                const res = await previewPrePurchase(r15Svc, { skuId, couponCode });
                return commit(SET_PRE_PURCHASE_PREVIEW, res);
            } catch (error) {
                return Promise.reject(error);
            }
        },
        [WRITE_REVIEW]({ getters }, router) {
            const { reviewableSkus, selectedChoice } = getters;
            const amountOfSkus = reviewableSkus.length;

            if (amountOfSkus === 1) {
                router.push({
                    name: rentalRouteNames.feedback.product,
                    params: {
                        scheme: REVIEW_SCHEMES.PRODUCT,
                        sku: reviewableSkus[0],
                        colorCode: get(selectedChoice, 'color.code'),
                    },
                });
            }

            if (amountOfSkus > 1) {
                router.push('#box-home-slide');
            }
        },
    },

    getters: {
        totalReviewResults(state) {
            return get(state, 'reviews.pagination.totalItems', 0);
        },
        totalSelectedFilters(state) {
            const selectedFilters = get(state, 'reviews.filterGroups', [])
                .map(filterGroup => filterGroup.selectedFilters);

            if (!selectedFilters.length) return 0;

            return selectedFilters
                .reduce((previousSelected, currentSelected) => previousSelected + currentSelected.length, 0);
        },
        getSelectedFilterGroup(state) {
            return filterGroupName => state.reviews.selectedFilterGroup === filterGroupName;
        },
        productHasNoReviews(state) {
            return !get(state.productDetails, 'reviewSummary.reviewCount');
        },
        selectedChoice(state) {
            if (!state.colorCode) return false;
            return state.productDetails?.choices.find(choice => choice.color.code === state.colorCode);
        },
        selectedChoiceSkus(state, getters) {
            const { selectedChoice } = getters;
            let skus = [];

            (selectedChoice?.sizeGroups || []).forEach((group) => {
                const groupSkus = group.includedSkus.map(e => e.skuId);
                skus = [...skus, ...groupSkus];
            });

            return skus;
        },
        reviewableSkus(state, getters, rootState, rootGetters) {
            const unreviewedRecentlyOrderedSkus = rootGetters[`${BOX_MODULE_NAME}/unreviewedRecentlyOrderedSkus`];
            const { selectedChoiceSkus } = getters;
            const skus = [];

            (selectedChoiceSkus || []).forEach((selectedChoiceSku) => {
                if (unreviewedRecentlyOrderedSkus.includes(selectedChoiceSku)) {
                    skus.push(selectedChoiceSku);
                }
            });

            return skus;
        },
        purchasableSkus(state, getters, rootState, rootGetters) {
            const shippedRecentlyOrderedSkus = rootGetters[`${BOX_MODULE_NAME}/shippedRecentlyOrderedSkus`];
            const boxIsDelivered = rootGetters[`${BOX_MODULE_NAME}/boxIsDelivered`];
            const { selectedChoiceSkus } = getters;
            const skus = [];

            if (boxIsDelivered) {
                (selectedChoiceSkus || []).forEach((selectedChoiceSku) => {
                    if (shippedRecentlyOrderedSkus.includes(selectedChoiceSku)) {
                        skus.push(selectedChoiceSku);
                    }
                });
            }

            return skus;
        },
        prePurchaseEligibleChoices(state) {
            return (state.productDetails?.choices || []).filter(choice => choice?.prePurchase?.eligible);
        },
        shouldShowWriteReviewLink(state, getters, rootState, rootGetters) {
            const boxIsDeliveredOrReturnStarted = rootGetters[`${BOX_MODULE_NAME}/boxIsDeliveredOrReturnStarted`];
            const { reviewableSkus } = getters;

            return reviewableSkus?.length && boxIsDeliveredOrReturnStarted;
        },
        isProductSoldOut(state, getters) {
            const { selectedChoice } = getters;

            if (!selectedChoice) return false;
            const availableSizeGroups = filterEmptySizeGroups(selectedChoice?.sizeGroups);

            return !availableSizeGroups?.length;
        },
        productAnalyticsData(state, getters) {
            const { productDetails, traceId, curation } = state;
            const { selectedChoice, isBuyMode } = getters;

            const {
                msrpValue,
                brandUrls,
                displayName,
                facets,
            } = productDetails;

            const productClass = get(facets, 'nuulyClass.0', '');

            return {
                product: {
                    productPrice: msrpValue,
                    productBrand: getProductBrandName(brandUrls || []),
                    productId: selectedChoice?.choiceId,
                    productName: displayName,
                    productAffiliation: PRODUCT_AFFILIATION,
                    productCategory: 'Women',
                    productCategory2: getProductDepartment(facets?.nuulyDepartment),
                    productCategory3: productClass,
                },
                traceId,
                business: isBuyMode ? PRODUCT_BUSINESSES.PREPURCHASE : PRODUCT_BUSINESSES.RENT,
                curation,
            };
        },
        productsRelatedToColorData(state, getters) {
            const { selectedChoice } = getters;

            return [...(selectedChoice?.productsRelatedToColor) || []];
        },
        hasProductsRelatedToColorData(state, getters) {
            const { selectedChoice } = getters;

            return !!selectedChoice?.productsRelatedToColor?.length;
        },
        isBuyMode(state) {
            return state.viewMode === PRODUCT_PAGE_VIEW_MODES.BUY;
        },
    },
};
